import * as React from 'react'

import { useSiteConfig, useSiteDefinition } from '@thg-commerce/enterprise-core'
import { COOKIE_GROUP_POWER_REVIEWS } from '@thg-commerce/enterprise-core/src/utils/CookieHelper/types'
import { ProductVariantWithDescription } from '@thg-commerce/enterprise-network/src/ApolloProvider/resolvers/Types/Product'
import { sanitizeCookie } from '@thg-commerce/enterprise-utils'
import { Direction } from '@thg-commerce/gravity-patterns/ProductBlock/types'

import { ContentItem } from '../../Product'

declare global {
  interface Window {
    pwr: (command: string, config: any) => void | any
  }
}

export enum PowerReviewGroups {
  PROPERTY = 'reviewsGroupingId',
}

export enum PowerReviewComponentType {
  REVIEW_DISPLAY = 'ReviewDisplay',
  REVIEW_SNIPPET = 'ReviewSnippet',
  REVIEW_CATEGORY_SNIPPET = 'CategorySnippet',
  REVIEW_WRITE_REVIEW = 'Write',
}

enum PowerReviewStatus {
  ENABLED = 'ENABLED',
  DISABLED = 'DISABLED',
}

export type PowerReviewProps = {
  externalId: number | string
  locale: string
  sku?: number
  component?: PowerReviewComponentType
  id?: string
  pathname?: string
  powerReviewGroups?: string
  product?: {
    selectedVariant: ProductVariantWithDescription
    variants: ProductVariantWithDescription[]
  }
  mainProduct?: boolean
  powerReviewsAlignment?: Direction
}

export function getPowerReviewGroups(content?: ContentItem[]) {
  const powerReviewGroupItem = content?.find(
    ({ key }) => key === PowerReviewGroups.PROPERTY,
  )
  if (powerReviewGroupItem) {
    const powerReviewGroupValue = powerReviewGroupItem.value as {
      __typename: 'ProductContentStringListValue'
      stringListValue: string[]
    }

    if (
      powerReviewGroupValue.stringListValue &&
      powerReviewGroupValue.stringListValue.length > 0
    ) {
      return powerReviewGroupValue.stringListValue[0]
    }
  }
  return
}

interface PowerReviewRenderConfig {
  ENABLE_CLIENT_SIDE_STRUCTURED_DATA: boolean
  api_key: string
  locale: string
  merchant_group_id: string
  merchant_id: string
  page_id: string | number
  sm_data: string
  enable_front_end_iovation_validation: boolean
  review_wrapper_url: string
  components: { [componentId in PowerReviewComponentType]?: string }
  product?: {
    name: string
    url: string
    image_url: string
    description?: string
    brand_name?: string
    upc?: string
    price?: string
    category_name?: string
    in_stock?: boolean
    variants?: {
      name: string
      image_url: string
      upc?: string
      page_id_variant?: string
    }[]
  }
}

const configBuilder = (config, selectedVariant, domain) =>
  (config.product = {
    name: selectedVariant?.powerReviewTitle ?? '',
    url: `${domain}${selectedVariant.url}`,
    image_url: selectedVariant.images[0]?.largeProduct,
    description: selectedVariant?.powerReviewDescription || undefined,
    brand_name: selectedVariant?.brand?.name || undefined,
    upc: selectedVariant.barcode || undefined,
    price: selectedVariant.price?.price?.amount || undefined,
    category_name: selectedVariant?.category || undefined,
    in_stock: selectedVariant?.deliveryStock || undefined,
  })

const configVariantBuilder = (config, product) =>
  (config.product.variants = product.variants.map((variant) => ({
    name: variant?.powerReviewTitle ?? '',
    image_url: variant.images[0]?.largeProduct,
    upc: variant.barcode || undefined,
    page_id_variant: variant.externalIdentifier || undefined,
  })))

const powerReviewStatus = (powerReviewValidation) =>
  powerReviewValidation ? PowerReviewStatus.ENABLED : PowerReviewStatus.DISABLED

const powerReviewValidation = (cookieGroupPattern, sanitizedValue) =>
  Boolean(cookieGroupPattern.test(sanitizedValue))

const buildConfig = (
  mainProduct,
  enablePowerReviewsSchemaRemoval,
  powerReviewApiKey,
  locale,
  powerReviewMerchantGroupId,
  powerReviewMerchantId,
  powerReviewGroups,
  externalId,
  cookieGroupPattern,
  sanitizedValue,
  domain,
  pathname,
  sku,
  product,
  enableSkuOnPowerReviews,
) => {
  const config: PowerReviewRenderConfig = {
    locale,
    ENABLE_CLIENT_SIDE_STRUCTURED_DATA: !(
      mainProduct || enablePowerReviewsSchemaRemoval
    ),
    api_key: powerReviewApiKey || '',
    merchant_group_id: powerReviewMerchantGroupId || '',
    merchant_id: powerReviewMerchantId || '',
    page_id: powerReviewGroups ?? enableSkuOnPowerReviews ? sku : externalId,
    sm_data: powerReviewStatus(
      powerReviewValidation(cookieGroupPattern, sanitizedValue),
    ),
    enable_front_end_iovation_validation: powerReviewValidation(
      cookieGroupPattern,
      sanitizedValue,
    ),
    review_wrapper_url: `/addReview.account?pr_return_url=${domain}${pathname}&productId=${
      sku ?? product?.selectedVariant.sku
    }&powerGroups=${powerReviewGroups}`,
    components: {},
  }

  return config
}

export const PowerReview = (props: PowerReviewProps) => {
  const {
    powerReviewApiUrl,
    powerReviewApiKey,
    powerReviewMerchantGroupId,
    powerReviewMerchantId,
    enablePowerReviewsSchemaRemoval,
    enableSkuOnPowerReviews,
  } = useSiteConfig()
  const { domain } = useSiteDefinition()

  const cookieGroupPattern = React.useMemo(() => {
    return new RegExp(`%2CC000${COOKIE_GROUP_POWER_REVIEWS}%2C`)
  }, [])

  const sanitizedValue = sanitizeCookie('actualOptanonConsent')

  React.useEffect(() => {
    window.pwr =
      window.pwr ||
      function () {
        ;(window.pwr.q = window.pwr.q || []).push(arguments)
      }

    const config: PowerReviewRenderConfig = buildConfig(
      props.mainProduct,
      enablePowerReviewsSchemaRemoval,
      powerReviewApiKey,
      props.locale,
      powerReviewMerchantGroupId,
      powerReviewMerchantId,
      props.powerReviewGroups,
      props.externalId,
      cookieGroupPattern,
      sanitizedValue,
      domain,
      props.pathname,
      props.sku,
      props.product,
      enableSkuOnPowerReviews,
    )

    if (props.component) {
      config.components[props.component] = props.id
      if (props.component === PowerReviewComponentType.REVIEW_SNIPPET) {
        config.components[PowerReviewComponentType.REVIEW_DISPLAY] =
          'pr-review-display'
      }
    }

    if (props.product) {
      const selectedVariant = props.product.selectedVariant
      configBuilder(config, selectedVariant, domain)

      if (props.product.variants) {
        configVariantBuilder(config, props.product)
      }
    }

    window.pwr('render', config)
  }, [
    domain,
    enablePowerReviewsSchemaRemoval,
    powerReviewApiKey,
    powerReviewMerchantGroupId,
    powerReviewMerchantId,
    cookieGroupPattern,
    sanitizedValue,
    props.product?.selectedVariant,
    props.component,
    props.externalId,
    props.id,
    props.locale,
    props.mainProduct,
    props.pathname,
    props.powerReviewGroups,
    props.product,
    props.sku,
    enableSkuOnPowerReviews,
  ])

  if (
    !powerReviewApiUrl ||
    !powerReviewApiKey ||
    !powerReviewMerchantGroupId ||
    !powerReviewMerchantId
  ) {
    return null
  }

  return <div id={props.id}></div>
}
